// Commit + undo engine for appointment mutations (drag-move, resize, editor
// saves). One code path does it all: optimistic local apply → atomic
// :update call → success toast with Undo (undo re-applies the previous values
// through the same path). Pending overrides survive refetches racing the API
// call (see reconcilePendingOverrides).

import { createContext, useCallback, useContext, useRef, useState } from "react";
import { toast } from "sonner";
import { updateAdminUserAppointment } from "@/lib/api/admin";
import { hasPermission } from "@/lib/stores/permissions-store";
import { publishCalendarRefresh } from "@/lib/realtime/appointment-realtime-bridge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { NIL_PATIENT_ID, effectiveLock15m } from "./calendar-utils";
import { AppointmentConfirmationDialog } from "./appointment-confirmation-dialog";

// How long an optimistic override may bridge refetches before we trust the
// server unconditionally again.
const OVERRIDE_TTL_MS = 15000;
// While the reason dialog is open the override must outlive WS refetches for
// as long as the user types — reset to the normal TTL once they confirm.
const REASON_PROMPT_TTL_MS = 10 * 60 * 1000;

// Keys of a `changes` object that are persisted via the update endpoint;
// anything else (e.g. a `doctor` display name, or the resolved `colorHex`
// riding along with a `colorHexOverride` change) is applied optimistically for
// display only and confirmed by the next refetch's enrichment.
const API_CHANGE_KEYS = ["startAt", "endAt", "title", "description", "status", "doctorId", "tenantId", "calendarId", "colorHexOverride", "lock15m"];

// Changes that count as a real amendment and therefore prompt for a
// modification reason (recorded to the appointment history). Cosmetic keys
// (color, the show-as-15-min display flag) stay prompt-free.
const REASON_CHANGE_KEYS = ["startAt", "endAt", "title", "description", "status", "doctorId", "tenantId", "calendarId"];

const REASON_QUICK_PICKS = [
  "Patient request",
  "Doctor unavailable",
  "Scheduling conflict",
  "Clinic request",
];

const CalendarMutationsContext = createContext(null);

export const CalendarMutationsProvider = CalendarMutationsContext.Provider;

export function useCalendarMutations() {
  return useContext(CalendarMutationsContext);
}

function apiPayloadFromChanges(changes) {
  const payload = {};
  if (changes.startAt !== undefined) payload.start = changes.startAt.toUTC().toISO();
  if (changes.endAt !== undefined) payload.end = changes.endAt.toUTC().toISO();
  if (changes.title !== undefined) payload.title = changes.title;
  if (changes.description !== undefined) payload.description = changes.description;
  if (changes.status !== undefined) payload.status = changes.status;
  if (changes.doctorId !== undefined) payload.doctorId = changes.doctorId;
  if (changes.tenantId !== undefined) payload.tenantId = changes.tenantId;
  if (changes.calendarId !== undefined) payload.calendarId = changes.calendarId;
  // The API stores the override at metadata.colorHex; "" clears it. The
  // event-level `colorHex` (override merged with the calendar fallback) is a
  // display-only sibling change and never goes out.
  if (changes.colorHexOverride !== undefined) payload.colorHex = changes.colorHexOverride;
  // Lock-first-15-minutes pin (metadata.lock15m).
  if (changes.lock15m !== undefined) payload.lock15m = changes.lock15m;
  return payload;
}

/** Whether this amendment warrants a reason prompt (real changes only). */
function changesNeedReason(changes) {
  return REASON_CHANGE_KEYS.some((key) => changes[key] !== undefined);
}

function valuesMatch(key, expected, actual) {
  if (key === "startAt" || key === "endAt") {
    const expectedMillis = expected?.toMillis?.();
    return Number.isFinite(expectedMillis) && expectedMillis === actual?.toMillis?.();
  }
  return String(expected ?? "").trim() === String(actual ?? "").trim();
}

/**
 * Re-apply still-pending optimistic overrides onto freshly fetched events so a
 * WS-triggered refetch racing an in-flight update doesn't snap cards back to
 * their old position mid-flight. Overrides the server already reflects (or
 * that expired) are dropped.
 */
export function reconcilePendingOverrides(fetchedEvents, overridesRef) {
  const overrides = overridesRef?.current;
  if (!overrides || overrides.size === 0) return fetchedEvents;
  const now = Date.now();
  for (const [id, entry] of overrides) {
    if (entry.expiresAt <= now) overrides.delete(id);
  }
  if (overrides.size === 0) return fetchedEvents;
  return fetchedEvents.map((event) => {
    const entry = overrides.get(event.id);
    if (!entry) return event;
    const serverCaughtUp = API_CHANGE_KEYS.every(
      (key) => entry.changes[key] === undefined || valuesMatch(key, entry.changes[key], event[key]),
    );
    if (serverCaughtUp) {
      overrides.delete(event.id);
      return event;
    }
    return { ...event, ...entry.changes };
  });
}

/**
 * Owns the single commit path for appointment mutations. `changes` is
 * event-shaped (Luxon startAt/endAt, plus ids/strings); display-only keys are
 * allowed and applied locally without being sent to the API.
 */
export function useCalendarAppointmentMutations({ setEvents }) {
  const pendingOverridesRef = useRef(new Map());

  // Pending reason prompt: { summary, resolve } while the dialog is open.
  // A ref-backed resolver keeps applyAppointmentUpdate's identity stable.
  const [reasonRequest, setReasonRequest] = useState(null);
  const promptReason = useCallback((summary) => {
    return new Promise((resolve) => {
      setReasonRequest({ summary, resolve });
    });
  }, []);

  // Confirmation draft dialog: { appointmentId, patientUserId, patientName, phone }
  // while open. Shared opener so the context menu, editor and slot-booking modal
  // all raise the same "review then send" flow.
  const [confirmationRequest, setConfirmationRequest] = useState(null);
  const requestAppointmentConfirmation = useCallback((target) => {
    const appointmentId = String(target?.appointmentId || "").trim();
    if (!appointmentId) return;
    setConfirmationRequest({
      appointmentId,
      patientUserId: String(target?.patientUserId || "").trim(),
      patientName: String(target?.patientName || "").trim(),
      phone: String(target?.phone || "").trim(),
    });
  }, []);

  const applyAppointmentUpdate = useCallback(
    async ({ event, changes, toastLabel, withUndo = true, reason }) => {
      if (!event?.id || !changes) return false;
      // RBAC: every appointment amendment (drag-move, resize, editor save, cancel, color,
      // lock-15m) funnels through here — the ONE calendar:manage gate. Checked via the store
      // (not a hook) so the callback identity stays stable.
      if (!hasPermission("calendar", "manage")) {
        toast.error("View only — your access level doesn't allow editing appointments.");
        return false;
      }
      const payload = apiPayloadFromChanges(changes);
      if (Object.keys(payload).length === 0) return false;

      // Values to restore on Undo / failure. Keys the event didn't carry
      // before the change stay undefined and are skipped by the API payload —
      // known limitation: a calendarId adopted from a slot drop can't be
      // "unset" by Undo (the endpoint has no clear-field semantics); the
      // doctor change in the same undo makes the backend re-derive it.
      const prev = {};
      for (const key of Object.keys(changes)) {
        prev[key] = event[key];
      }
      // An appointment with no color override undoes to an explicit clear ("")
      // — leaving it undefined would drop colorHex from the undo payload.
      if (changes.colorHexOverride !== undefined && prev.colorHexOverride === undefined) {
        prev.colorHexOverride = "";
      }
      // A never-pinned lock-15m flag undoes to the pre-change EFFECTIVE
      // state — MetadataMerge can't unset metadata.lock15m, and an undefined
      // prev value would make the whole undo payload empty (no-op).
      if (changes.lock15m !== undefined && prev.lock15m === undefined) {
        prev.lock15m = effectiveLock15m(event);
      }
      // Status can't round-trip undefined either (the API accepts only
      // "scheduled"/"cancelled") — undoing a cancel of a status-less
      // appointment restores it to "scheduled", same as the Restore action.
      if (changes.status !== undefined && prev.status === undefined) {
        prev.status = "scheduled";
      }

      // Optimistic apply — date buckets and overlap lanes are memos derived
      // from `events`, so they recompute on their own.
      setEvents((current) => current.map((e) => (e.id === event.id ? { ...e, ...changes } : e)));
      pendingOverridesRef.current.set(event.id, {
        changes,
        expiresAt: Date.now() + OVERRIDE_TTL_MS,
      });

      // Every real amendment asks for a reason (recorded to the appointment's
      // history) unless the caller already supplied one (e.g. Undo). The
      // optimistic apply above stays visible while the dialog is open; a
      // dismissed dialog rolls it back — nothing was sent yet.
      let modificationReason = typeof reason === "string" ? reason : undefined;
      if (modificationReason === undefined && changesNeedReason(changes)) {
        const promptedEntry = pendingOverridesRef.current.get(event.id);
        if (promptedEntry?.changes === changes) {
          promptedEntry.expiresAt = Date.now() + REASON_PROMPT_TTL_MS;
        }
        const entered = await promptReason(toastLabel || "Update appointment");
        const pendingEntry = pendingOverridesRef.current.get(event.id);
        if (entered == null) {
          // Same supersede guard as the failure path: only roll back if OUR
          // override is still the pending one.
          if (pendingEntry?.changes === changes) {
            pendingOverridesRef.current.delete(event.id);
            setEvents((current) => current.map((e) => (e.id === event.id ? { ...e, ...prev } : e)));
          }
          return false;
        }
        modificationReason = entered;
        if (pendingEntry?.changes === changes) {
          pendingEntry.expiresAt = Date.now() + OVERRIDE_TTL_MS;
        }
      }

      try {
        const response = await updateAdminUserAppointment({
          // NEW-patient bookings and phone-matched Plato snapshots have no
          // addressable patient user — the zero id is the endpoint's sentinel
          // for "skip the patient sanity check".
          userId: String(event.patientId || "").trim() || NIL_PATIENT_ID,
          appointmentId: event.id,
          ...payload,
          ...(modificationReason ? { reason: modificationReason } : {}),
        });
        const conflicts = Array.isArray(response?.conflicts) ? response.conflicts : [];
        toast.success(toastLabel || "Appointment updated", {
          id: `appointment-update-${event.id}`,
          duration: 8000,
          description: conflicts.length
            ? `Overlaps ${conflicts.length} other appointment${conflicts.length === 1 ? "" : "s"} for this doctor.`
            : undefined,
          action: withUndo
            ? {
                label: "Undo",
                onClick: () => {
                  applyAppointmentUpdate({
                    event: { ...event, ...changes },
                    changes: prev,
                    toastLabel: "Change undone",
                    withUndo: false,
                    // Undo must not re-open the reason dialog — record it as such.
                    reason: "Undo of previous change",
                  });
                },
              }
            : undefined,
        });
        publishCalendarRefresh({ source: "appointment-update", appointmentId: event.id });
        return true;
      } catch (error) {
        // Only roll back if OUR override is still the pending one — a newer
        // move of the same appointment may have replaced it mid-flight, and
        // reverting then would clobber the newer optimistic state. The
        // `changes` object reference doubles as this call's token.
        const pendingEntry = pendingOverridesRef.current.get(event.id);
        if (pendingEntry?.changes === changes) {
          pendingOverridesRef.current.delete(event.id);
          setEvents((current) => current.map((e) => (e.id === event.id ? { ...e, ...prev } : e)));
        }
        toast.error("Failed to update appointment", {
          id: `appointment-update-${event.id}`,
          description: error?.message || undefined,
        });
        return false;
      }
    },
    [setEvents, promptReason],
  );

  const reasonDialogElement = (
    <AppointmentReasonDialog
      request={reasonRequest}
      onDone={(value) => {
        reasonRequest?.resolve(value);
        setReasonRequest(null);
      }}
    />
  );

  const confirmationDialogElement = (
    <AppointmentConfirmationDialog
      open={confirmationRequest != null}
      onOpenChange={(next) => {
        if (!next) setConfirmationRequest(null);
      }}
      appointmentId={confirmationRequest?.appointmentId}
      patientUserId={confirmationRequest?.patientUserId}
      patientName={confirmationRequest?.patientName}
      phone={confirmationRequest?.phone}
    />
  );

  return {
    applyAppointmentUpdate,
    requestAppointmentConfirmation,
    pendingOverridesRef,
    reasonDialogElement,
    confirmationDialogElement,
  };
}

/**
 * Modal asking why the appointment is being amended. Resolves with the entered
 * text ("" allowed — the change still commits) or null when dismissed (the
 * optimistic apply is rolled back and nothing is sent). Text state is keyed on
 * the request object so every prompt starts blank.
 */
function AppointmentReasonDialog({ request, onDone }) {
  const [text, setText] = useState("");
  const lastRequestRef = useRef(null);
  if (request !== lastRequestRef.current) {
    lastRequestRef.current = request;
    if (request && text !== "") {
      setText("");
    }
  }

  const open = request != null;
  const confirm = () => onDone(text.trim());

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onDone(null);
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Reason for this change</DialogTitle>
          <DialogDescription>
            {request?.summary || "Update appointment"} — the reason is saved to the
            appointment&apos;s history.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-1">
          <div className="flex flex-wrap gap-1.5">
            {REASON_QUICK_PICKS.map((pick) => (
              <button
                key={pick}
                type="button"
                onClick={() => setText(pick)}
                className="rounded-full border border-border bg-muted/40 px-2.5 py-1 text-xs font-medium text-muted-foreground transition-colors hover:border-primary/40 hover:bg-primary/10 hover:text-foreground"
              >
                {pick}
              </button>
            ))}
          </div>
          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Why is this appointment being changed?"
            rows={3}
            autoFocus
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                confirm();
              }
            }}
          />
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onDone(null)}>
            Cancel change
          </Button>
          <Button type="button" onClick={confirm}>
            {text.trim() ? "Save change" : "Save without reason"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
